import type { CreatePatientDTO, Patient, UpdatePatientDTO } from "../domain/Patient";
import { CouplePatientAPI } from "../infrastructure/api/CouplePatientApiService";
import { PatientsAPI, type ListPatientsParams } from "../infrastructure/api/patients.api";

export async function getPatient(id: string) {
  const { data } = await PatientsAPI.get(id);
  return data;
}

/** A page of the therapist's caseload, with the total count. */
export async function listTherapistPatients(therapistId: string, params?: ListPatientsParams) {
  const { data } = await PatientsAPI.list_by_therapist(therapistId, params);
  return data;
}

/** The API's largest page. */
const ALL_PATIENTS_PAGE = 1000;

/**
 * The therapist's whole caseload, for a picker or a search over it. Asked for
 * page by page: the API answers ten when no limit is given, and a picker that
 * stopped at a page left students out without saying so.
 */
export async function listAllTherapistPatients(therapistId: string, params?: Omit<ListPatientsParams, "offset" | "limit">) {
  const patients: Patient[] = [];
  for (let offset = 0; ; offset += ALL_PATIENTS_PAGE) {
    const { data } = await PatientsAPI.list_by_therapist(therapistId, { ...params, offset, limit: ALL_PATIENTS_PAGE });
    const page = data?.patients ?? [];
    patients.push(...page);
    const total = data?.total ?? patients.length;
    if (page.length < ALL_PATIENTS_PAGE || patients.length >= total) {
      return { patients, total: Math.max(total, patients.length) };
    }
  }
}

/** The same, shaped like the API's answer for callers that read `.data`. */
export async function listAllTherapistPatientsResponse(therapistId: string) {
  return { data: await listAllTherapistPatients(therapistId) };
}

export async function createPatient(payload: CreatePatientDTO) {
  const { data } = await PatientsAPI.create(payload);
  return data;
}

export async function updatePatient(id: string, payload: UpdatePatientDTO) {
  const { data } = await PatientsAPI.update(id, payload);
  return data;
}

export async function deletePatient(id: string) {
  await PatientsAPI.delete(id);
}

/** Everything held about the patient, as a JSON blob to download. */
export async function exportPatient(id: string) {
  const { data } = await PatientsAPI.export(id);
  return data;
}

export async function listTherapistCouples(therapistId: string) {
  const { data } = await CouplePatientAPI.listByTherapist(therapistId);
  return data;
}

export async function deleteCouplePatient(id: string) {
  await CouplePatientAPI.delete(id);
}
